import {
  formatPrice,
  PROMOTION_TOWN_KM,
  promotionPlan,
  type PromotionDoc,
  type PromotionPlanId,
} from '@talkofthetown/shared';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { useCurrency } from '../hooks/useCurrency';
import { usePromote } from '../hooks/usePromote';
import { PlanPicker } from './PlanPicker';

interface Props {
  postId: string;
  uid: string;
  /** Closes the post: Razorpay's checkout opens on the page, not inside a dialog. */
  onLeave: () => void;
}

const until = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
});

/** The promotion the author paid for that's waiting or running, if any. */
export async function currentPromotion(postId: string, uid: string): Promise<PromotionDoc | null> {
  const snap = await getDocs(
    query(collection(db, 'promotions'), where('postId', '==', postId), where('uid', '==', uid)),
  );
  return (
    snap.docs
      .map((d) => d.data() as PromotionDoc)
      .filter((p) => p.status === 'review' || p.status === 'active')
      .sort((a, b) => b.createdAt - a.createdAt)[0] ?? null
  );
}

/** How a paid promotion is doing, in a few words. */
export function promotionStatusText(p: PromotionDoc): string {
  return p.status === 'review'
    ? 'Promotion paid: it starts as soon as we’ve had a quick look (or you get a full refund).'
    : `Promoted: whispered more, marked Sponsored, until ${until.format(p.endsAt ?? 0)}.`;
}

/**
 * In an author's own post: pay to have it whispered more for a while (Razorpay), or see how the
 * promotion they paid for is doing.
 */
export function PromotePanel({ postId, uid, onLeave }: Props) {
  const promote = usePromote();
  const [current, setCurrent] = useState<PromotionDoc | null | undefined>(undefined);
  const [choosing, setChoosing] = useState(false);
  const [plan, setPlan] = useState<PromotionPlanId | null>('town-1d');
  const [currency, setCurrency] = useCurrency();

  useEffect(() => {
    let cancelled = false;
    currentPromotion(postId, uid)
      .then((p) => !cancelled && setCurrent(p))
      .catch(() => !cancelled && setCurrent(null));
    return () => {
      cancelled = true;
    };
  }, [postId, uid]);

  function pay() {
    if (!plan) return;
    onLeave();
    void promote(postId, plan, currency);
  }

  if (current === undefined) return null;
  if (current) {
    return (
      <p className="promote__status" role="status">
        {promotionStatusText(current)}
      </p>
    );
  }
  if (!choosing) {
    return (
      <button type="button" className="btn promote__open" onClick={() => setChoosing(true)}>
        Promote this post
      </button>
    );
  }

  const chosen = plan ? promotionPlan(plan) : null;
  return (
    <fieldset className="promote">
      <legend>Promote this post</legend>
      <p className="muted">
        It’s whispered more: a yellow whisper marked Sponsored that stays on screen longer and comes
        round more often. If people open it, it gets a voice like any post. “Around your town” means
        readers within {PROMOTION_TOWN_KM} km.
      </p>
      <PlanPicker
        value={plan}
        onChange={setPlan}
        currency={currency}
        onCurrencyChange={setCurrency}
      />
      <p className="muted promote__note">
        We check every promoted post before it shows; if we don’t approve it, you get a full refund.{' '}
        {currency === 'INR'
          ? 'Pay by UPI (Google Pay, PhonePe, Paytm…), card or netbanking.'
          : 'Pay by card.'}
      </p>
      <div className="promote__actions">
        <button type="button" className="btn btn--primary" disabled={!chosen} onClick={pay}>
          {chosen ? `Pay ${formatPrice(chosen.prices[currency], currency)}` : 'Pay'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => setChoosing(false)}>
          Not now
        </button>
      </div>
    </fieldset>
  );
}
